// mui
import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// types
import { RecurrenceFrequency } from '@karehero/models';

const frequencyLabel: Record<RecurrenceFrequency, string> = {
  daily: 'days',
  weekly: 'weeks',
  monthly: 'months',
};

export interface RecurrenceControlsProps {
  frequency: RecurrenceFrequency;
  onFrequencyChange?: (value: RecurrenceFrequency) => void;
  interval: number;
  onIntervalChange?: (value: number) => void;
  isDisabled?: boolean;
}

/**
 * RecurrenceControls renders the "Repeats every N days/weeks/months" picker.
 * Used in both create mode and repeating-task edit mode.
 */
export const RecurrenceControls = ({
  frequency,
  onFrequencyChange,
  interval,
  onIntervalChange,
  isDisabled = false,
}: RecurrenceControlsProps) => (
  <Stack
    direction='row'
    spacing={2}
    sx={{
      alignItems: 'center',
    }}
  >
    <Typography variant='body2'>Repeats every</Typography>
    <TextField
      type='number'
      value={interval === 0 ? '' : interval}
      disabled={isDisabled}
      onChange={(e) => {
        const val = e.target.value;
        if (val === '') {
          onIntervalChange?.(0);
        } else {
          onIntervalChange?.(Math.min(365, Math.max(0, parseInt(val) || 0)));
        }
      }}
      error={!interval || interval < 1}
      slotProps={{
        htmlInput: { min: 1, max: 365 },
      }}
      sx={{ width: 80 }}
      size='small'
    />
    <FormControl size='small' sx={{ minWidth: 120 }}>
      <Select
        value={frequency}
        disabled={isDisabled}
        onChange={(e) =>
          onFrequencyChange?.(e.target.value as RecurrenceFrequency)
        }
      >
        <MenuItem value='daily'>{frequencyLabel.daily}</MenuItem>
        <MenuItem value='weekly'>{frequencyLabel.weekly}</MenuItem>
        <MenuItem value='monthly'>{frequencyLabel.monthly}</MenuItem>
      </Select>
    </FormControl>
  </Stack>
);
